const VISUAL_STYLE_PROPS = [
  'border-top-width',
  'border-top-style',
  'border-right-width',
  'border-right-style',
  'border-bottom-width',
  'border-bottom-style',
  'border-left-width',
  'border-left-style',
  'font-weight',
  'font-size',
  'font-family',
  'font-variant-numeric',
  'line-height',
  'text-decoration-line',
  'text-decoration-thickness',
  'text-underline-offset',
  'text-align',
  'vertical-align',
  'white-space',
  'border-radius',
] as const;

/** 테이블 구조는 레이아웃 속성 복사 제외 — 캡처 높이 0 방지 */
function copyComputedVisualStyles(
  source: HTMLElement,
  target: HTMLElement,
  options?: InlineVisualStylesOptions
): void {
  const computed = window.getComputedStyle(source);
  const style = target.style;
  const skipColors = options?.skipColors ?? false;

  if (!skipColors) {
    const color = computed.color;
    if (color) style.color = color;

    const backgroundColor = computed.backgroundColor;
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      style.backgroundColor = backgroundColor;
    }

    const borderColors = ['borderTopColor', 'borderRightColor', 'borderBottomColor', 'borderLeftColor'] as const;
    for (const key of borderColors) {
      const value = computed[key];
      if (value) {
        style[key] = value;
      }
    }

    const textDecorationColor = computed.textDecorationColor;
    if (textDecorationColor) {
      style.textDecorationColor = textDecorationColor;
    }
  }

  for (const prop of VISUAL_STYLE_PROPS) {
    if (options?.skipFontSize && prop === 'font-size') continue;
    const value = computed.getPropertyValue(prop);
    if (!value) continue;
    style.setProperty(prop, value);
  }
}

export type InlineVisualStylesOptions = {
  /** PDF/프린트 시 캡처 CSS·라이트 셸 색상을 유지 */
  skipColors?: boolean;
  /** PDF/프린트 시 캡처 CSS의 font-size를 유지 (메타 제목 등) */
  skipFontSize?: boolean;
};

/** 모달·문서 PDF/프린트 캡처 시 레이아웃만 복사하고 색상은 캡처 CSS에 맡김 */
export const DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS: InlineVisualStylesOptions = {
  skipColors: true,
};

/** 메타 제목 캡처 — font-size는 캡처 CSS가 화면과 동일·이상 크기로 지정 */
export const DOCUMENT_EXPORT_META_INLINE_STYLE_OPTIONS: InlineVisualStylesOptions = {
  skipColors: true,
  skipFontSize: true,
};

/** DOM 순서가 같은 두 트리 — 모달 화면 computed style을 캡처 클론에 인라인 반영 */
export function inlineVisualStylesFromSource(
  sourceRoot: HTMLElement,
  targetRoot: HTMLElement,
  options?: InlineVisualStylesOptions
): void {
  const sourceNodes: Element[] = [sourceRoot, ...Array.from(sourceRoot.querySelectorAll('*'))];
  const targetNodes: Element[] = [targetRoot, ...Array.from(targetRoot.querySelectorAll('*'))];
  const count = Math.min(sourceNodes.length, targetNodes.length);

  for (let index = 0; index < count; index += 1) {
    const source = sourceNodes[index];
    const target = targetNodes[index];
    if (!(source instanceof HTMLElement) || !(target instanceof HTMLElement)) continue;
    copyComputedVisualStyles(source, target, options);
  }
}
